<script setup lang="ts">
/**
 * The way forward from an invalid design outside Shape (run 2, p09/p11; PLAN §3, §6 "never a dead
 * button"): the reason, the family's or the generic fix, and — when a material change broke a
 * valid design — "Keep <the previous material>". Look shows it as a block under the material;
 * Pack shows it in the dock instead of a blocked export with no way forward.
 */
import { computed } from 'vue';
import Icon from './Icon.vue';
import { snap } from '@/app/state';
import { announce } from '@/app/announce';
import { setMaterialTo } from '@/app/edits';
import { materialBreak, realFix } from '@/app/fixes';
import { focusSoon, go } from '@/app/navigation';

const props = defineProps<{ where: 'look' | 'pack' }>();
const invalid = computed(() => !snap.value.build.validation.ok);
const mb = computed(() => { void snap.value; return materialBreak(); });
const fx = computed(() => { void snap.value; return realFix(); });
const reason = computed(() => mb.value?.message ?? fx.value?.reason ?? snap.value.build.validation.errors[0]?.message ?? '');

function applyFix() {
  fx.value?.act();
  focusSoon(props.where === 'look' ? '#matH' : '#dock-export');
}
function keep() {
  const m = mb.value;
  if (!m) return;
  setMaterialTo(m.from);
  announce(`Kept ${m.fromLabel.toLowerCase()}. The design is valid again.`);
  focusSoon(`[data-material="${m.from}"], #dock-export`);
}
</script>

<template>
  <div v-if="invalid" class="fix-actions" :class="`fix-actions--${where}`" :role="where === 'look' ? 'alert' : undefined" data-design-invalid>
    <p class="fix-reason" data-fix-reason><Icon name="alert" /><span>{{ reason }}</span></p>
    <div class="fix-btns">
      <button v-if="fx" type="button" class="btn btn--ink" :class="where === 'pack' ? 'btn--lg' : 'btn--sm'" data-act="fix" :data-fix="fx.kind" @click="applyFix">{{ fx.label }}</button>
      <button v-if="mb" type="button" class="btn" :class="[fx ? 'btn--quiet' : 'btn--ink', where === 'pack' ? 'btn--lg' : 'btn--sm']" data-act="keep-material" @click="keep">Keep {{ mb.fromLabel.toLowerCase() }}</button>
      <button v-if="!fx && !mb" type="button" class="btn btn--ink" :class="where === 'pack' ? 'btn--lg' : 'btn--sm'" data-act="fix-in-shape" @click="go('shape')">Fix it in Shape <Icon name="arrow" /></button>
    </div>
  </div>
</template>
